import { Component, ChangeDetectionStrategy, input, output, computed, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Clipboard, ClipboardModule } from '@angular/cdk/clipboard';
import { Field, StructureType } from '../../../types/fields';

@Component({
  selector: 'app-structure-card',
  imports: [
    MatIconModule,
    MatTooltipModule,
    MatSnackBarModule,
    ClipboardModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div data-testid="structure-card"
         class="group relative flex flex-col gap-4 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-5 shadow-sm transition-all hover:shadow-md hover:border-zinc-300 dark:hover:border-zinc-600"
         (mouseenter)="hovered.emit(structure())"
         (mouseleave)="hovered.emit(null)">
      @if (isDeleting()) {
        <div class="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white/70 dark:bg-zinc-800/70 backdrop-blur-[1px]">
          <div class="animate-spin rounded-full h-5 w-5 border-2 border-zinc-300 dark:border-zinc-500 border-t-red-500"></div>
        </div>
      }
      <!-- Header -->
      <div class="flex items-start justify-between">
        <div class="flex items-start gap-3">
          <div
            class="h-10 w-1.5 rounded-full shrink-0 mt-1"
            [style.backgroundColor]="structure().color"
          ></div>
          <div class="flex flex-col">
            <h3 data-testid="structure-card-name" class="font-semibold text-zinc-900 dark:text-zinc-100 text-lg leading-tight">{{ structure().name }}</h3>
            <span data-testid="structure-card-field-count" class="text-xs text-zinc-500 dark:text-zinc-400 mt-1">{{ associatedFields().length }} fields</span>
          </div>
        </div>

        <!-- Actions: always visible, emphasized on hover -->
        <div class="flex items-center gap-0.5">
          <button
            data-testid="structure-card-edit-btn"
            type="button"
            (click)="edit.emit(structure())"
            matTooltip="Edit"
            class="flex items-center justify-center h-7 w-7 rounded-md text-zinc-400 dark:text-zinc-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <mat-icon class="!text-base !w-4 !h-4 !leading-4">edit</mat-icon>
          </button>
          <button
            data-testid="structure-card-copy-btn"
            type="button"
            (click)="copyToClipboard()"
            matTooltip="Copy to clipboard"
            class="flex items-center justify-center h-7 w-7 rounded-md text-zinc-400 dark:text-zinc-500 hover:text-violet-600 dark:hover:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/30 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
          >
            <mat-icon class="!text-base !w-4 !h-4 !leading-4">content_copy</mat-icon>
          </button>
          <button
            data-testid="structure-card-delete-btn"
            type="button"
            (click)="delete.emit(structure())"
            matTooltip="Delete"
            class="flex items-center justify-center h-7 w-7 rounded-md text-zinc-400 dark:text-zinc-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
          >
            <mat-icon class="!text-base !w-4 !h-4 !leading-4">delete</mat-icon>
          </button>
        </div>
      </div>

      <!-- Description -->
      @if (structure().description) {
        <p class="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
          {{ structure().description }}
        </p>
      }

      <!-- Associated Fields -->
      <div class="mt-2 pt-3 border-t border-zinc-100 dark:border-zinc-700">
        <div class="flex flex-wrap gap-2">
          @if (associatedFields().length > 0) {
            @for (field of visibleFields(); track field.id) {
              <span class="inline-flex items-center gap-1.5 rounded-md bg-zinc-50 dark:bg-zinc-700 px-2 py-1 text-xs text-zinc-600 dark:text-zinc-300 border border-zinc-100 dark:border-zinc-600">
                <span class="h-1.5 w-1.5 rounded-full shrink-0" [style.backgroundColor]="structure().color"></span>
                {{ field.name }}
              </span>
            }
            @if (associatedFields().length > visibleFieldLimit) {
              <span class="inline-flex items-center rounded-md bg-zinc-50 dark:bg-zinc-700 px-2 py-1 text-xs text-zinc-400 border border-zinc-100 dark:border-zinc-600">
                +{{ associatedFields().length - visibleFieldLimit }} more
              </span>
            }
          } @else {
            <span class="text-xs text-zinc-400 dark:text-zinc-500 italic">No fields associated</span>
          }
        </div>
      </div>
    </div>
  `
})
export class StructureCardComponent {
  private clipboard = inject(Clipboard);
  private snackBar = inject(MatSnackBar);

  structure = input.required<StructureType>();
  allFields = input.required<Field[]>();

  isDeleting = input<boolean>(false);

  edit = output<StructureType>();
  delete = output<StructureType>();
  /** Emits the structure on mouseenter and null on mouseleave — used to highlight its fields. */
  hovered = output<StructureType | null>();

  readonly visibleFieldLimit = 6;

  associatedFields = computed(() => {
    const fieldIds = this.structure().fieldIds;
    return this.allFields().filter(f => fieldIds.includes(f.id));
  });

  visibleFields = computed(() => {
    return this.associatedFields().slice(0, this.visibleFieldLimit);
  });

  copyToClipboard(): void {
    const structure = this.structure();
    const structureData = [{
      id: structure.id,
      name: structure.name,
      description: structure.description || '',
      fieldIds: structure.fieldIds,
      color: structure.color
    }];
    const json = JSON.stringify(structureData, null, 2);
    this.clipboard.copy(json);
    this.snackBar.open('Structure copied to clipboard', 'OK', { duration: 2000 });
  }
}
